<script setup>
import usePdfGenerator from '@/utils/usePdfGenerator'
import { computed, defineProps } from 'vue'
import { useStore } from '@/utils/VuexHelpers'
import { formatCurrency, formatDecimals } from '@/helpers/stringHelpers'

const props = defineProps({
  single: {
    type: Boolean,
    default: false
  },
  singleFreight: {
    type: Boolean,
    default: false
  },
  summary: {
    type: Object,
    required: true
  },
  transportList: {
    type: Array,
    default: () => []
  },
  freightCode: {
    type: Function,
    required: true
  }
})

const { generatingPdf, exportPdf, titleDatePart } = usePdfGenerator()

const store = useStore()
const freightNames = computed(() => store.getters.freightNames)
const filtersTransp = computed(() => store.getters.filtersTransp)
const startDateTransp = computed(() => store.getters.startDateTransp)
const freightFilterInfo = computed(() => store.getters.freightFilterInfo)
const loadingSuppliers = computed(() => store.getters.loadingSuppliers)

const exportLabel = computed(() => {
  if (props.single) {
    return `Export for ${freightFilterInfo.value?.code || '-'}`
  }

  return 'Export'
})

const freightFilterTotal = (total) => {
  if (!total) return ''
  if (isNaN(total)) return 0

  return formatDecimals(total)
}

const totalsHeader = computed(() => {
  let headers = ['Type', 'Weight', 'Quantity', 'Price']

  if (props.single) {
    headers = headers.slice(0, 3)
  }

  if (props.singleFreight) {
    headers.push(`Price for ${freightFilterInfo.value?.code || '-'}`)
  }

  return headers
})

const totalsTable = computed(() => {
  const totals = []
  const pigsWeight = props.summary.pigsWeight || 0
  const cartonsWeight = props.summary.cartonsWeight || 0
  const pigs = props.summary.pigs || 0
  const splitPricePigs = props.summary.splitPricePigs || 0
  const cartons = props.summary.cartons || 0
  const pigsPrice = props.summary.pigsPrice || 0
  const cartonsPrice = props.summary.cartonsPrice || 0
  const splitPriceCartons = props.summary.splitPriceCartons || 0

  const totalWeight = pigsWeight + cartonsWeight
  const totalQty = pigs + cartons
  const totalPrice = pigsPrice + cartonsPrice
  const totalSplitPrice = splitPriceCartons + splitPricePigs

  let lineColor = [200]
  let lineWidth = { top: 0.2 }
  let fontStyle = 'bolditalic'
  let pigsRow = [
    {
      content: 'Pigs',
      styles: { fontStyle, lineWidth, lineColor }
    },
    {
      content: `${formatDecimals(pigsWeight)}kg`,
      styles: { lineWidth, lineColor }
    },
    {
      content: pigs,
      styles: { lineWidth, lineColor }
    },
    {
      content: formatCurrency(pigsPrice),
      styles: { lineWidth, lineColor }
    }
  ]
  let cartonsRow = [
    {
      content: 'Cartons',
      styles: { fontStyle }
    },
    `${formatDecimals(cartonsWeight)}kg`,
    cartons,
    formatCurrency(cartonsPrice)
  ]

  fontStyle = 'bold'
  lineColor = [0, 0, 0]
  let totalsRow = [
    {
      content: 'Total',
      styles: { fontStyle, lineWidth, lineColor }
    },
    {
      content: `${formatDecimals(totalWeight)}kg`,
      styles: { lineWidth, lineColor }
    },
    {
      content: totalQty,
      styles: { lineWidth, lineColor }
    },
    {
      content: formatCurrency(totalPrice),
      styles: { lineWidth, lineColor }
    }
  ]

  if (props.single) {
    pigsRow = pigsRow.slice(0, 3)
    cartonsRow = cartonsRow.slice(0, 3)
    totalsRow = totalsRow.slice(0, 3)
  }

  if (props.singleFreight) {
    lineColor = [200]
    pigsRow.push({
      content: formatCurrency(splitPricePigs),
      styles: { lineWidth, lineColor }
    })
    cartonsRow.push(formatCurrency(splitPriceCartons))
    lineColor = [0, 0, 0]
    totalsRow.push({
      content: formatCurrency(totalSplitPrice),
      styles: { lineWidth, lineColor }
    })
  }

  totals.push(pigsRow)
  totals.push(cartonsRow)
  totals.push(totalsRow)

  return totals
})

const tableHeaders = computed(() => {
  const styles = {
    halign: 'right'
  }
  const headers = [
    'Customer',
    { content: 'Weight (kg)', styles },
    { content: 'Cartons', styles },
    { content: 'Pigs', styles },
    { content: 'Price ($)', styles },
    'Delivered By',
    'Invoice Nr.'
  ]

  if (props.single) {
    return headers.slice(0, 4)
  }

  if (props.singleFreight) {
    headers.splice(5, 0, {
      content: `Price ($) - ${freightFilterInfo.value?.code || '-'}`,
      styles
    })
  }

  return headers
})

const tableItems = computed(() => {
  const styles = {
    halign: 'right'
  }

  return props.transportList.map((item) => {
    const {
      total_weight,
      total_cartons,
      total_pigs,
      total_ex_GST,
      filteredPrice
    } = item.job_description
    const row = [
      item.destination?.name || '',
      { content: formatDecimals(total_weight || 0), styles },
      { content: total_cartons || ' ', styles },
      { content: total_pigs || ' ', styles },
      { content: formatDecimals(total_ex_GST || 0), styles }
    ]
    row.push(props.freightCode(item.delivery_co_ids))
    row.push(item.invoice || '-')

    if (props.single) {
      return row.slice(0, 4)
    }

    if (props.singleFreight) {
      row.splice(5, 0, {
        content: freightFilterTotal(filteredPrice),
        styles
      })
    }

    return row
  })
})

const subTitle = computed(() => {
  const datePart = titleDatePart(startDateTransp.value, filtersTransp.value)
  return `TRANSPORT SUMMARY ${datePart}`
})

const fileName = computed(() => {
  const datePart = titleDatePart(
    startDateTransp.value,
    filtersTransp.value,
    true
  )
  return `Transport-Summary-[${datePart}]`
})

const generatePdf = () => {
  const config = {
    totalsTable: true,
    totalsTableHeaders: totalsHeader.value,
    totalsTitle: props.single ? freightFilterInfo.value?.name || '' : null,
    theme: 'striped',
    halign: 'left',
    tableFontSize: 8,
    orientation: 'portrait'
  }

  exportPdf(
    totalsTable.value,
    subTitle.value,
    tableItems.value,
    tableHeaders.value,
    fileName.value,
    config
  )
}

// ON CREATED
if (
  freightNames.value.length === 0 &&
  loadingSuppliers.value !== 'freightnames'
) {
  store.dispatch('fetchFreightNames')
}
</script>

<template>
  <v-btn
    small
    :color="single ? undefined : 'warning'"
    :loading="generatingPdf"
    @click="generatePdf"
  >
    <font-awesome-icon icon="fas fa-file-export" class="mr-2" />
    {{ exportLabel }}
  </v-btn>
</template>
